import { ERRORS } from "../../../../constants/errors";
import type { Answers, Groups } from "../../../../features/questionnaire";
import type { Answer } from "../../Section/Question/types";
import { getErrorMessage, ok } from "../question/rules";
import { validateQuestion } from "../../../validation/question/validate";
import { getCompareFunction } from "./compare";
import { evaluateExpression } from "./expressionEvaluator";
import type {
  Combinator,
  EvaluateRule,
  PartialQuestion,
  Rule,
  RuleSet,
  VisibilityMap,
} from "./types";
import { isRule, isRuleSet } from "./types";
import { shouldValidateNonVisible } from "./shouldValidateNonVisible";

export type ValidateQuestionWithFormRulesParams = {
  question: PartialQuestion;
  answers: Answers;
  visibilityMap: VisibilityMap;
  locale?: string;
  groups?: Groups;
};

// Pre-generate rule evaluation functions
const compileRule = (rule: Rule): EvaluateRule => {
  const evaluateRule = getCompareFunction(rule.operator);

  return (answers: Answers, visibilityMap: VisibilityMap = {}) => {
    // Skip evaluation if this question is not visible
    if (!visibilityMap[rule.id]) {
      return {
        id: rule.id,
        valid: true, // Consider invisible questions as valid
        message: undefined,
      };
    }

    const compareValue = rule.expression
      ? evaluateExpression(rule.expression, { answers, visibilityMap }) // from expression evaluation
      : rule.value; // from a literal value

    // Determine the left-hand side value
    const leftHandValue =
      rule.leftValue !== undefined
        ? rule.leftValue // Use literal value (cast as Answer type)
        : rule.leftExpression
          ? evaluateExpression(rule.leftExpression, { answers, visibilityMap }) // Use expression
          : answers[rule.id]; // Default: use answer value (existing behavior)

    const valid =
      leftHandValue == null || compareValue == null
        ? false
        : evaluateRule(leftHandValue, compareValue);

    return {
      id: rule.id,
      valid,
      message: valid && isRuleSet(rule) ? undefined : (rule as RuleSet).message,
    };
  };
};

const compileCombinator = (combinator: Combinator) => {
  const compiledRules: EvaluateRule[] = combinator.rules.map(
    ruleOrCombinator => {
      if (isRule(ruleOrCombinator)) {
        return compileRule(ruleOrCombinator);
      }
      return compileCombinator(ruleOrCombinator);
    },
  );

  return (answers: Answers, visibilityMap: VisibilityMap = {}) => {
    const results = compiledRules.map(rule => rule(answers, visibilityMap));
    const visibleResults = results.filter(
      result => visibilityMap[result.id] !== false,
    );

    // If no visible rules to evaluate, consider it valid
    if (visibleResults.length === 0) {
      return {
        id: combinator.id,
        valid: true,
        message: undefined,
      };
    }

    const valid =
      combinator.combinator === "and"
        ? visibleResults.every(result => result.valid)
        : visibleResults.some(result => result.valid);

    return {
      id: combinator.id,
      valid,
      message:
        valid && isRuleSet(combinator)
          ? undefined
          : (combinator as RuleSet).message,
    };
  };
};

// Pre-generate rule set (combination) evaluation functions
const compileRuleSet = (ruleSet: RuleSet) => {
  return isRule(ruleSet) ? compileRule(ruleSet) : compileCombinator(ruleSet);
};

const isRequiredButEmpty = (
  question: PartialQuestion,
  answer?: Answer,
): boolean => {
  if (!question.visible && !question.suggested) return false;
  if (!(question.required || question.isRequired)) return false;

  return !answer || (Array.isArray(answer) && !answer.length);
};

// Factory function to create a validator from rules data
export const createValidator = (validations: readonly RuleSet[]) => {
  const compiledRuleset = validations.map(compileRuleSet);

  return ({
    question,
    answers,
    visibilityMap,
    locale,
    groups,
  }: ValidateQuestionWithFormRulesParams) => {
    const answer = answers[question.name];

    // Skip validation if question is not visible, or if visibilityMap indicates hidden
    // (with exception for non-visible questions that should still validate)
    if (
      (!question.visible && !question.suggested) ||
      (visibilityMap?.[question.name] === false &&
        !shouldValidateNonVisible({
          question,
          groups,
          answers,
          visibilityMap,
        }))
    ) {
      return ok(question.name);
    }

    if (isRequiredButEmpty(question, answer)) {
      return {
        id: question.name,
        valid: false,
        message: getErrorMessage({
          questionErrorMessage: question.errorMessage,
          fallbackMessage: ERRORS.REQUIRED,
        }),
      };
    }

    // Question-level validation rules
    const localValidation = validateQuestion({
      question,
      answers,
      locale,
      groups,
    });

    if (!localValidation.valid) {
      return localValidation;
    }

    // Find the first form-level rule that fails
    const formValidation = compiledRuleset
      .map(ruleFunction => ruleFunction(answers, visibilityMap))
      .filter(
        result => result.id === question.name && result.valid === false,
      )[0];

    return formValidation ?? ok(question.name);
  };
};
